import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../components/Button'
import { api } from '../api/client'
import { DESTINATION_OPTIONS, FORMAT_OPTIONS, SOURCE_OPTIONS, addExclude, addInclude, defaultsDestination, formBrief, isWebUrl, scheduleBrief, supportsMembership, withDestinationFor, withFormat, type Brief, type SourceRef } from '../lib/brief'
import { hitRef, hitTitle, refKey, refTitle, type ArchiveHit } from '../lib/reviewSources'
import { ArchiveSearch } from './ReviewSources'

export function DraftBriefForm({ initial, title, onSubmit, onCancel, busy, saveDefaults = true, submitLabel = 'Draft it', publicationSlug = 'flight_journal', sectionLabel = 'Flight Line', scheduleMode = false }: {
  initial: Brief; title: string; onSubmit: (b: Brief) => void; onCancel: () => void; busy: boolean; saveDefaults?: boolean; submitLabel?: string; publicationSlug?: string; sectionLabel?: string
  /** The schedule's brief: each run picks its own subject and nobody reviews it, so there is no source
   *  picker, link field or review checkbox (the server drops pins, blocks and review-first anyway). */
  scheduleMode?: boolean
}) {
  const [b, setB] = useState<Brief>(formBrief(initial))
  const [saved, setSaved] = useState(false)
  // Review-first follows the format (on for Deep) until the editor ticks or clears it.
  const [touchedReview, setTouchedReview] = useState(false)
  // Titles of the pinned/blocked refs, for the chips only; the brief carries bare refs.
  const [titles, setTitles] = useState<Record<string, string>>({})
  const [link, setLink] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  const pin = (ref: SourceRef, title?: string) => {
    const out = addInclude(b, ref)
    setPinError(out.error ?? null)
    if (out.error) return false
    if (title) setTitles((t) => ({ ...t, [refKey(ref)]: title }))
    setB(out.brief)
    return true
  }
  const block = (h: ArchiveHit) => {
    setPinError(b.exclude.length >= 30 ? 'At most 30 blocked sources.' : null)
    setTitles((t) => ({ ...t, [refKey(hitRef(h))]: hitTitle(h) }))
    setB(addExclude(b, hitRef(h)))
  }
  const unlist = (which: 'include' | 'exclude', ref: SourceRef) =>
    setB({ ...b, [which]: b[which].filter((r) => refKey(r) !== refKey(ref)) })
  const addLink = () => {
    const url = link.trim()
    if (!isWebUrl(url)) { setPinError('Paste a full http:// or https:// link (500 characters at most).'); return }
    if (pin({ kind: 'web', url })) setLink('')
  }
  const webAllowed = b.sources !== 'internal'
  const [webDropped, setWebDropped] = useState(false)
  // Our content has no web: pinned links go, with a note saying so.
  const withSources = (sources: Brief['sources']): Brief => {
    const dropped = sources === 'internal' && b.include.some((r) => r.kind === 'web')
    setWebDropped(dropped)
    return dropped ? { ...b, sources, include: b.include.filter((r) => r.kind !== 'web') } : { ...b, sources }
  }
  const qc = useQueryClient()
  const save = useMutation({
    // The saved defaults the drafts page already loaded: a title without membership keeps their destination.
    mutationFn: (v: Brief) => api.put(`/api/lab/section-drafts/my-defaults?publication_slug=${publicationSlug}`, {
      format: v.format, sources: v.sources,
      destination: defaultsDestination(v, publicationSlug, qc.getQueryData<Brief>(['section-my-defaults'])?.destination),
    }),
    onSuccess: () => { setSaved(true); qc.invalidateQueries({ queryKey: ['section-my-defaults'] }) },
  })
  // Changing format or sources after saving makes the label stale.
  const change = (next: Brief) => { if (next.format !== b.format || next.sources !== b.sources || next.destination !== b.destination) { setSaved(false); save.reset() } setB(next) }
  const radio = (name: 'format' | 'sources' | 'destination', opts: readonly { key: string; label: string; hint: string }[]) => (
    <fieldset className="space-y-1">
      <legend className="label">{name === 'format' ? 'Format' : name === 'sources' ? 'Sources' : 'Publish to'}</legend>
      {opts.map((o) => (
        <label key={o.key} className="flex items-start gap-2 text-[13px] text-paper">
          <input type="radio" name={name} checked={b[name] === o.key} onChange={() => change(name === 'format' ? withFormat(b, o.key as Brief['format'], touchedReview) : name === 'sources' ? withSources(o.key as Brief['sources']) : { ...b, destination: o.key as Brief['destination'] })} />
          <span>{o.label}<span className="label normal-case tracking-normal block">{o.hint}</span></span>
        </label>
      ))}
    </fieldset>
  )
  return (
    <form className="border border-ink-600 bg-ink-900 rounded p-4 space-y-3"
          onSubmit={(e) => { e.preventDefault(); onSubmit(withDestinationFor(scheduleMode ? scheduleBrief(b) : b, publicationSlug)) }}>
      <div className="text-paper text-[14px]">{submitLabel === 'Draft it' ? 'Draft' : 'Brief for'} <strong>{title}</strong></div>
      <div className={`grid gap-4 ${supportsMembership(publicationSlug) ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {radio('format', FORMAT_OPTIONS)}{radio('sources', SOURCE_OPTIONS)}
        {supportsMembership(publicationSlug) && radio('destination', DESTINATION_OPTIONS(sectionLabel))}
      </div>
      <label className="block">
        <span className="label">Notes for the writer (optional)</span>
        <textarea rows={2} maxLength={1000} value={b.guidance} onChange={(e) => setB({ ...b, guidance: e.target.value })}
                  placeholder="e.g. focus on the carrier trials; aim it at first-time builders"
                  className="mt-1 w-full bg-ink-900 border border-ink-600 text-paper text-[13px] p-1.5" />
      </label>
      {!scheduleMode && <div className="space-y-2">
        <span className="label">Sources to use (optional)</span>
        <ArchiveSearch publicationSlug={publicationSlug} onUse={(h) => pin(hitRef(h), hitTitle(h))} onBlock={block} />
        <div className="flex items-center gap-2">
          <input type="url" value={link} onChange={(e) => setLink(e.target.value)} disabled={!webAllowed} maxLength={500}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (webAllowed) addLink() } }}
                 placeholder="Paste a web link" className="flex-1 bg-ink-900 border border-ink-600 text-paper text-[13px] p-1.5" />
          <Button type="button" size="sm" variant="outline" disabled={!webAllowed || !link.trim()} onClick={addLink}>Add</Button>
        </div>
        {webDropped && <p className="text-amber-200 text-[12px]">Web links removed — they need The web or Both</p>}
        {!webAllowed && <p className="label normal-case tracking-normal">Web links need The web or Both</p>}
        {pinError && <p className="text-red-300 text-[12px]">{pinError}</p>}
        {(b.include.length > 0 || b.exclude.length > 0) && (
          <ul className="flex flex-wrap gap-2">
            {b.include.map((r) => (
              <li key={`in-${refKey(r)}`} className="flex items-center gap-1 border border-emerald-700 rounded px-2 py-0.5 text-[12px] text-emerald-200 max-w-full">
                <span className="uppercase text-[10px]">use</span> <span className="truncate">{refTitle(r, titles)}</span>
                <button type="button" aria-label={`Stop using ${refTitle(r, titles)}`} className="ml-1" onClick={() => unlist('include', r)}>×</button>
              </li>
            ))}
            {b.exclude.map((r) => (
              <li key={`ex-${refKey(r)}`} className="flex items-center gap-1 border border-red-800 rounded px-2 py-0.5 text-[12px] text-red-200 max-w-full">
                <span className="uppercase text-[10px]">don't use</span> <span className="truncate">{refTitle(r, titles)}</span>
                <button type="button" aria-label={`Allow ${refTitle(r, titles)} again`} className="ml-1" onClick={() => unlist('exclude', r)}>×</button>
              </li>
            ))}
          </ul>
        )}
      </div>}
      {!scheduleMode && <label className="flex items-center gap-2 text-[13px] text-paper">
        <input type="checkbox" checked={b.review_sources}
               onChange={(e) => { setTouchedReview(true); setB({ ...b, review_sources: e.target.checked }) }} />
        Let me review sources first
        <span className="label normal-case tracking-normal">stops after gathering sources so you can remove or add some before it writes</span>
      </label>}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={busy}>{busy ? (saveDefaults ? 'A draft is being written…' : 'Saving…') : submitLabel}</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        {saveDefaults && (
          <span className="ml-auto flex items-center gap-2">
            {save.isError && <span className="text-red-300 text-[12px]">Could not save your defaults.</span>}
            <button type="button" className="label underline" disabled={save.isPending} onClick={() => save.mutate(b)}>
              {saved ? 'Saved as your defaults' : 'Save as my defaults'}
            </button>
          </span>
        )}
      </div>
    </form>
  )
}
